import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Loader2, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { agentApi, type AgentChat } from '@/lib/agent.api';
import { getApiErrorMessage } from '@/lib/http';
import { SidebarExpandButton } from '@/components/SidebarExpandButton';
import { RainbowBeamsBackdrop } from '@/components/RainbowBeamsBackdrop';

/**
 * /sessions — the default-org home for a user's conversations. Lists every
 * 'general' agent session (the chats the Home composer mints and hands off to
 * /session/:id). A session that has spun up a job is jobId-bound; opening it
 * continues the conversation inside that job's manage workspace. Hidden system
 * chats (auto-sourcing carriers) are excluded server-side.
 */
function formatRelative(iso: string | null) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    if (Number.isNaN(diff) || diff < 0) return '';
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
}

export function SessionsListPage() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<AgentChat[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);

    const fetchSessions = useCallback(async () => {
        setError(null);
        try {
            const { results } = await agentApi.list({ agentType: 'general', limit: 100, includeTotal: false });
            setSessions(results);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not load sessions.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchSessions();
    }, [fetchSessions]);

    function openSession(session: AgentChat) {
        // A job-bound session lives in that job's manage workspace (where the
        // same conversation resumes); an unbound one opens the standalone chat.
        if (session.jobId) navigate(`/v2/jobs/${session.jobId}/manage`);
        else navigate(`/session/${session._id}`);
    }

    async function handleDelete(sessionId: string) {
        setPendingDelete(sessionId);
        try {
            await agentApi.archive(sessionId);
            setSessions((current) => current.filter((s) => s._id !== sessionId));
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not archive session.'));
        } finally {
            setPendingDelete(null);
        }
    }

    return (
        <div className="relative isolate flex min-h-full w-full flex-col overflow-hidden bg-[var(--surface-primary)] font-[family-name:var(--font-sans)] text-[color:var(--text-primary)]">
            <RainbowBeamsBackdrop />
            <header className="flex items-baseline justify-between gap-4 border-b border-[color:var(--border-muted)] px-10 pb-6 pt-8">
                <div className="flex items-start gap-2.5">
                    <SidebarExpandButton style={{ marginTop: 4, marginLeft: -8 }} />
                    <div>
                        <h1 className="m-0 font-[family-name:var(--font-serif)] text-[32px] font-medium tracking-[-0.01em]">
                            Sessions
                        </h1>
                        <p className="mt-1.5 text-[13px] text-[color:var(--text-secondary)]">
                            Every conversation you&apos;ve started with Lantern. Pick up where you left off.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-3.5 py-2 text-[13px] font-medium text-white"
                >
                    <Plus size={14} strokeWidth={2} />
                    New session
                </button>
            </header>

            {error ? (
                <div className="mx-10 mt-4 rounded-lg bg-[var(--bg-danger)] px-3.5 py-2.5 text-[13px] text-[color:var(--fg-danger)]">
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div className="flex flex-1 items-center justify-center text-[color:var(--text-tertiary)]">
                    <Loader2 size={18} strokeWidth={1.75} className="animate-spin" />
                </div>
            ) : sessions.length === 0 ? (
                <EmptyState onStart={() => navigate('/')} />
            ) : (
                <div className="flex flex-col gap-2 px-10 pb-10 pt-4">
                    {sessions.map((session) => (
                        <SessionRow
                            key={session._id}
                            session={session}
                            isDeleting={pendingDelete === session._id}
                            onOpen={() => openSession(session)}
                            onDelete={() => handleDelete(session._id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function EmptyState({ onStart }: { onStart: () => void }) {
    return (
        <div className="flex flex-1 items-center justify-center p-10">
            <div className="max-w-[420px] text-center text-[color:var(--text-tertiary)]">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--surface-tertiary)] text-[color:var(--text-secondary)]">
                    <MessageSquare size={20} strokeWidth={1.6} />
                </span>
                <p className="m-0 text-[15px] font-medium text-[color:var(--text-primary)]">No sessions yet</p>
                <p className="mx-0 mb-0 mt-2 text-[13px] leading-relaxed text-[color:var(--text-secondary)]">
                    Start a session to hire, source, or plan with Lantern. Each session captures the full
                    conversation and any job it spins up.
                </p>
                <button
                    type="button"
                    onClick={onStart}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-3.5 py-2 text-[13px] font-medium text-white"
                >
                    <Plus size={14} strokeWidth={2} />
                    New session
                </button>
            </div>
        </div>
    );
}

function SessionRow({
    session,
    isDeleting,
    onOpen,
    onDelete,
}: {
    session: AgentChat;
    isDeleting: boolean;
    onOpen: () => void;
    onDelete: () => void;
}) {
    return (
        <div className="flex items-center gap-3 rounded-xl border border-[color:var(--border-muted)] bg-[var(--surface-primary)] px-4 py-3">
            <button
                type="button"
                onClick={onOpen}
                className="flex min-w-0 flex-1 items-center gap-3 border-0 bg-transparent p-0 text-left text-inherit"
            >
                <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--surface-tertiary)] text-[color:var(--text-secondary)]">
                    <MessageSquare size={15} strokeWidth={1.7} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                    <strong className="truncate text-[14px] font-medium text-[color:var(--text-primary)]">
                        {session.title || 'Untitled session'}
                    </strong>
                    <span className="mt-0.5 flex items-center gap-2 text-[12px] text-[color:var(--text-tertiary)]">
                        <span>{formatRelative(session.lastMessageAt ?? session.updatedAt)}</span>
                        {session.jobId ? (
                            <>
                                <span>·</span>
                                <span className="inline-flex items-center gap-1 text-[color:var(--text-secondary)]">
                                    <Briefcase size={11} strokeWidth={1.8} />
                                    Job linked
                                </span>
                            </>
                        ) : null}
                    </span>
                </span>
            </button>
            <button
                type="button"
                onClick={onDelete}
                disabled={isDeleting}
                aria-label={`Archive ${session.title || 'session'}`}
                className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-[color:var(--text-tertiary)] disabled:opacity-50"
            >
                {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.7} />}
            </button>
        </div>
    );
}

export default SessionsListPage;
